import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Avatar } from '../../components/ui/Avatar';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton, SkeletonKPIRow, SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { useUrlState } from '../../hooks/useUrlState';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useKanbanDnd } from '../../lib/dnd';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { fmtDate } from '../../lib/utils';
import {
  tasksSummaryQuery, tasksListQuery, assignableUsersQuery, clientsLiteQuery,
  SCOPES, SCOPE_LABEL, dueInfo, contextLine, shortName, fullName, isDone, isHigh, incollaPiuRighe,
  type TaskItem, type TaskScope, type TaskDueFilter, type TaskListResponse,
} from './tasksApi';
import { useTaskActions } from './useTaskActions';
import { CalendarFeedPopover } from './CalendarFeed';
import { TasksMobile } from './TasksMobile';
import { leggibile } from '../../components/mobile/MobileKit';
import './tasks.css';

/**
 * Pagina Task — stessa architettura della Contabilità:
 *   page-ed-hd  → titolo + sottotitolo + CTA "Nuovo task"
 *   stat-band   → Da fare / Scadono oggi / In ritardo / Completati questa settimana (click = filtro)
 *   vista       → Lista (tab Aperti | Completati) oppure Kanban a due colonne
 *                 Da fare / Completate; scelta persistita in localStorage
 *   filterbar   → vista · scope Miei / Assegnati da me / Tutti (pill) · cerca · assegnatario · priorità · cliente
 *   quick add   → titolo + assegnatario (io) + scadenza, Invio per creare (lista);
 *                 in kanban vive in cima alla colonna Da fare
 *
 * Un task è "da fare" oppure "completato": gli stati intermedi del modello
 * (IN_PROGRESS, REVIEW, BLOCKED) qui contano come aperti.
 *
 * Stato in URL: tab · scope · due · q · assignee · client · prio · ordina
 *   es. /tasks?scope=all&due=overdue · /tasks?tab=done
 *
 * Ordina e filtra (24/9): la lista è paginata, quindi ordina l'API (`sort`).
 * Scadenza, priorità e assegnatario stanno in Filtra per con le STESSE chiavi
 * di prima (due, prio, assignee): i numeri in alto e i link salvati continuano
 * ad aprire la lista giusta. La priorità prima filtrava solo la pagina che si
 * vedeva, adesso la filtra l'API su tutta la lista. La bacheca non si ordina
 * (in Da fare conta l'ordine del trascinamento), ma i filtri valgono anche lì.
 */

const URL_DEFAULTS: Record<string, string> = { tab: 'open', scope: 'mine', due: '', q: '', assignee: '', client: '', prio: '' };
const PAGE_SIZE = 25;
const VIEW_KEY = 'tasks-view';

const ORDINI_APERTI: OpzioneOrdina[] = [
  { key: 'scadenza', label: 'Scadenza più vicina prima' },
  { key: 'priorita', label: 'Prima la priorità alta' },
  { key: 'assegnato', label: 'Assegnato a, dalla A alla Z' },
  { key: 'recenti', label: 'Creati più di recente prima' },
  { key: 'titolo', label: 'Titolo dalla A alla Z' },
];
const ORDINI_COMPLETATI: OpzioneOrdina[] = [
  { key: 'completati', label: 'Completati più di recente prima' },
  { key: 'assegnato', label: 'Assegnato a, dalla A alla Z' },
  { key: 'titolo', label: 'Titolo dalla A alla Z' },
];

const FILTRO_SCADENZA: FiltroDef = {
  param: 'due', label: 'Scadenza',
  opzioni: [
    { value: 'overdue', label: 'Solo quelli in ritardo' },
    { value: 'today', label: 'Solo quelli che scadono oggi' },
    { value: 'week', label: 'Solo quelli che scadono entro 7 giorni' },
    { value: 'none', label: 'Solo quelli senza scadenza' },
  ],
};
const FILTRO_PRIORITA: FiltroDef = {
  param: 'prio', label: 'Priorità',
  opzioni: [
    { value: 'ALTA', label: 'Solo la priorità alta' },
    { value: 'NORMALE', label: 'Solo la priorità normale' },
  ],
};

/**
 * Sul telefono i Task sono TasksMobile (raggruppati per scadenza, niente
 * riquadri di numeri). Sul computer resta questa, identica.
 */
export function TasksPage() {
  const isMobile = useIsMobile();
  return isMobile ? <div className="page"><TasksMobile /></div> : <TasksDesktop />;
}

function TasksDesktop() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const [, setSearchParams] = useSearchParams();

  const canWrite = useMemo(() => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === ModuleKey.TASKS)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.WRITE);
  }, [user]);

  // ── Stato vista → URL (bookmarkabile; tornando dal dettaglio i filtri restano) ──
  const [tabParam] = useUrlState<string>('tab', 'open');
  const tab: 'open' | 'done' = tabParam === 'done' ? 'done' : 'open';
  const [scopeParam] = useUrlState<string>('scope', 'mine');
  const scope: TaskScope = (SCOPES as string[]).includes(scopeParam) ? (scopeParam as TaskScope) : 'mine';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [client, setClient] = useUrlState<string>('client', '');

  // Vista persistita: chi lavora per liste non deve rifare il toggle a ogni visita.
  const [view, setViewState] = useState<'list' | 'kanban'>(() => (localStorage.getItem(VIEW_KEY) === 'kanban' ? 'kanban' : 'list'));
  const setView = (v: 'list' | 'kanban') => { setViewState(v); localStorage.setItem(VIEW_KEY, v); };

  // ── Ordina e filtra ──
  const { data: users } = useQuery(assignableUsersQuery);
  // Fra i completati l'ordine di sempre è la data di chiusura, non la scadenza
  const ordina = useOrdina(tab === 'done' && view === 'list' ? ORDINI_COMPLETATI : ORDINI_APERTI);
  // "In ritardo" o "scade oggi" fra i completati non vuol dire niente: la
  // scadenza si filtra sugli aperti (e nella bacheca, che li ha tutti e due)
  const conScadenza = view === 'kanban' || tab === 'open';
  const filtriDefs = useMemo<FiltroDef[]>(() => [
    ...(conScadenza ? [FILTRO_SCADENZA] : []),
    FILTRO_PRIORITA,
    {
      param: 'assignee', label: 'Assegnato a',
      opzioni: (users ?? []).map((u) => ({ value: u.id, label: fullName(u) })),
    },
  ], [conScadenza, users]);
  const filtri = useFiltri(filtriDefs);
  const dueParam = filtri.valore('due');
  const due: TaskDueFilter = (['today', 'overdue', 'week', 'none'] as string[]).includes(dueParam) ? (dueParam as TaskDueFilter) : '';
  const assignee = filtri.valore('assignee');
  const prioParam = filtri.valore('prio');
  const prio: 'ALTA' | 'NORMALE' | undefined = prioParam === 'ALTA' || prioParam === 'NORMALE' ? prioParam : undefined;

  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [tab, scope, search, client, filtri.chiave, ordina.key]);

  // Aggiornamento atomico di più param (un solo history entry)
  const setParamsBatch = (next: Partial<Record<'tab' | 'scope' | 'due' | 'q' | 'assignee' | 'client' | 'prio', string>>) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(next)) {
        if (v == null || v === URL_DEFAULTS[k]) sp.delete(k);
        else sp.set(k, v);
      }
      return sp;
    });
  };
  const clearFilters = () => setParamsBatch({ due: '', q: '', assignee: '', client: '', prio: '' });
  const hasFilters = !!(search.trim() || client || filtri.attivi > 0);

  // ── Dati ──
  const { data: summary, isLoading: loadingSummary } = useQuery(tasksSummaryQuery(scope));
  const { data: clientsData } = useQuery(clientsLiteQuery);

  const baseParams = useMemo(() => ({
    scope,
    assigneeId: assignee || undefined,
    clientId: client || undefined,
    search: search.trim() || undefined,
    due: due || undefined,
    priorita: prio,
  }), [scope, assignee, client, search, due, prio]);

  // Lista: Aperti = tutto ciò che non è DONE (includeDone=false); Completati = solo DONE
  const listParams = useMemo(() => ({
    ...baseParams,
    status: tab === 'done' ? 'DONE' : undefined,
    includeDone: tab === 'done',
    sort: ordina.key,
    page,
    pageSize: PAGE_SIZE,
  }), [baseParams, tab, ordina.key, page]);
  // Kanban: aperti e completati insieme, un'unica pagina larga
  const kanbanParams = useMemo(() => ({ ...baseParams, includeDone: true, page: 1, pageSize: 200 }), [baseParams]);

  const listQ = useQuery({ ...tasksListQuery(listParams), enabled: view === 'list' });
  const kanbanQ = useQuery({ ...tasksListQuery(kanbanParams), enabled: view === 'kanban' });

  const listItems = listQ.data?.items ?? [];
  const kanbanItems = kanbanQ.data?.items ?? [];

  const doneTotal = summary?.byStatus?.DONE;
  const openNew = () => openModal('newTask');
  const meId = user?.id ?? '';

  return (
    <div className="page">
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Task</h1>
          <div className="ed-sub">Cosa c'è da fare, chi lo fa, entro quando.</div>
        </div>
        {/* d-only: sul telefono l'azione principale non sta in cima, dove il
            pollice non arriva, ma nella barra in fondo alla pagina. */}
        <div className="flex gap-2 pb-1 items-center d-only">
          {canWrite && (
            <button className="btn btn-primary" onClick={openNew}>
              <Icon name="plus" /> Nuovo task
            </button>
          )}
          <CalendarFeedPopover />
        </div>
      </div>

      {loadingSummary && !summary ? (
        <SkeletonKPIRow count={4} withIcon={false} />
      ) : (
        <div className="stat-band">
          <StatCell label="Da fare" value={summary?.todo ?? 0} sub="aperti" onClick={() => setParamsBatch({ tab: 'open', due: '' })} />
          <StatCell label="Scadono oggi" value={summary?.dueToday ?? 0} sub="entro stasera" onClick={() => setParamsBatch({ tab: 'open', due: 'today' })} />
          {/* Rosso = solo eccezione vera: c'è almeno un task in ritardo */}
          <StatCell label="In ritardo" value={summary?.overdue ?? 0} sub={(summary?.overdue ?? 0) > 0 ? 'da recuperare' : 'nessuno'} subAlert={(summary?.overdue ?? 0) > 0} onClick={() => setParamsBatch({ tab: 'open', due: 'overdue' })} />
          <StatCell label="Completati questa settimana" value={summary?.doneThisWeek ?? 0} sub="da lunedì" onClick={() => { setView('list'); setParamsBatch({ tab: 'done', due: '' }); }} />
        </div>
      )}

      {/* In kanban le due colonne sono già Aperti / Completate: le tab servono solo alla lista */}
      {view === 'list' && (
        <div className="page-tabs">
          <div className="page-tab" aria-selected={tab === 'open'} onClick={() => setParamsBatch({ tab: 'open', due: due === 'overdue' || due === 'today' ? due : '' })}>
            Aperti{summary != null && <span className="c">{summary.todo}</span>}
          </div>
          <div className="page-tab" aria-selected={tab === 'done'} onClick={() => setParamsBatch({ tab: 'done', due: '' })}>
            Completati{doneTotal != null && <span className="c">{doneTotal}</span>}
          </div>
        </div>
      )}

      {/* Sul telefono i filtri stanno dietro un tasto solo: in fila sono cinque
          righe da quaranta pixel che spingono la lista sotto la piega, e la
          lista è quello per cui si apre questa pagina. Sul computer
          MobileFilters restituisce i suoi figli così come sono. */}
      <MobileFilters attivi={filtri.attivi + [search.trim(), client].filter(Boolean).length} label="Cerca, ordina e filtra">
      <div className={`filterbar filterbar--float mb-4 flex-wrap${view === 'kanban' ? ' mt-4' : ''}`}>
        <div className="tweak-seg" role="group" aria-label="Vista">
          <button aria-pressed={view === 'list'} onClick={() => setView('list')}><Icon name="list" size={12} /> Lista</button>
          <button aria-pressed={view === 'kanban'} onClick={() => setView('kanban')}><Icon name="kanban" size={12} /> Kanban</button>
        </div>

        <div className="divider" />

        {/* Scope: di chi sono i task */}
        <div className="flex items-center gap-1.5" role="group" aria-label="Di chi">
          {SCOPES.map((s) => (
            <button key={s} onClick={() => setParamsBatch({ scope: s })} aria-pressed={scope === s} className={scope === s ? 'filter-pill is-active' : 'filter-pill'}>
              {SCOPE_LABEL[s]}
            </button>
          ))}
        </div>

        {/* 130 e non i 150 delle altre barre elastiche (trattative, progetti,
            vault, impostazioni): qui sulla riga ci sono anche tre pill e tre
            tendine. Misurato a 1440: con 150 la barra va a capo e la pagina
            cresce di 28px. I venti pixel in meno sono voluti, non una svista. */}
        <div className="tb-search" style={{ flex: '1 1 130px', minWidth: 130, maxWidth: 300, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca task" placeholder="Cerca…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        {/* Il cliente resta una tendina: i clienti sono decine, in un menu
            con le spunte sarebbero una lista da scorrere. Assegnatario e
            priorità stanno in Filtra per. */}
        <select className="inp text-[12px] py-1" style={{ width: 140, height: 32 }} value={client} onChange={(e) => setClient(e.target.value)} aria-label="Cliente">
          <option value="">Cliente</option>
          {(clientsData?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.shortName}</option>)}
        </select>

        <span className="mute text-[12px]">
          {view === 'list' ? `${listQ.data?.total ?? 0} task` : `${kanbanItems.filter((t) => !isDone(t.status)).length} da fare`}
        </span>

        <div className="spacer" />
        <OrdinaFiltra ordina={view === 'list' ? ordina : undefined} filtri={filtri} />
      </div>
      </MobileFilters>

      {view === 'list' ? (
        <>
          {canWrite && tab === 'open' && <QuickAdd users={users ?? []} meId={meId} />}
          <ListView
            query={listQ}
            items={listItems}
            tab={tab}
            page={page}
            onPage={setPage}
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
            canWrite={canWrite}
            onNew={openNew}
            onOpen={(id) => navigate(`/tasks/${id}`)}
          />
        </>
      ) : (
        // La bacheca si usa trascinando le schede da una colonna all'altra, e
        // il trascinamento è quello nativo del browser: col dito non parte.
        // Sul telefono al suo posto compare una riga che lo dice, come fa la
        // bacheca delle trattative; sul computer non cambia niente.
        <DesktopOnly cosa="La bacheca" perche="i task si spostano trascinandoli da una colonna all'altra, e col dito non si trascina. Dal telefono resta la lista, dove ogni task si chiude con un tocco.">
        <KanbanView
          queryKey={tasksListQuery(kanbanParams).queryKey}
          isLoading={kanbanQ.isLoading && !kanbanQ.data}
          items={kanbanItems}
          canWrite={canWrite}
          meId={meId}
          hasFilters={hasFilters}
          onClearFilters={clearFilters}
          onOpen={(id) => navigate(`/tasks/${id}`)}
        />
        </DesktopOnly>
      )}

      {/* Telefono: l'azione principale della schermata, appoggiata sopra la
          barra di navigazione. m-only la spegne sul computer, dove la stessa
          azione sta già in cima. */}
      {canWrite && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={openNew}>
            <Icon name="plus" /> Nuovo task
          </button>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Stat-band cell (numero nudo cliccabile = filtro)
// ════════════════════════════════════════════════════════════════════
function StatCell({ label, value, sub, subAlert, onClick }: {
  label: string; value: number; sub: string; subAlert?: boolean; onClick: () => void;
}) {
  return (
    <div className="st-click" role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}>
      <div className="st-label">{label}</div>
      <div className="st-value"><AnimatedNumber value={value} /></div>
      <div className={`st-delta${subAlert ? ' text-alert' : ''}`}>{sub}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Quick add: titolo + assegnatario (default: io) + scadenza · Invio crea
//
// d-only: sul telefono non c'è. Tre campi in colonna sono 140px di modulo
// sopra la lista, e l'azione "nuovo task" è già in fondo allo schermo, dove
// arriva il pollice (os-planning/mobile.md §4). Scrivere al volo è un gesto
// da tastiera vera: qui si viene a leggere e a chiudere task.
// ════════════════════════════════════════════════════════════════════
function QuickAdd({ users, meId }: { users: Array<{ id: string; firstName: string; lastName: string }>; meId: string }) {
  const { quickCreate } = useTaskActions();
  const [title, setTitle] = useState('');
  const [assigneeId, setAssigneeId] = useState(meId);
  const [dueDate, setDueDate] = useState('');
  useEffect(() => { if (!assigneeId && meId) setAssigneeId(meId); }, [meId, assigneeId]);

  const submit = () => {
    const t = title.trim();
    if (!t || quickCreate.isPending) return;
    quickCreate.mutate({ title: t, assigneeId: assigneeId || null, dueDate: dueDate || null });
    setTitle(''); // assegnatario e data restano: salva-e-aggiungi-un-altro
  };

  return (
    <form className="tk-quick d-only" onSubmit={(e) => { e.preventDefault(); submit(); }} aria-label="Aggiungi task al volo">
      <div className="tk-quick-title">
        <Icon name="plus" size={12} className="mute" />
        <input
          className="inp"
          placeholder="Nuovo task: scrivi il titolo e premi Invio"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setTitle(''); }}
          // Più righe incollate: la finestra con l'anteprima, con persona e scadenza già scelte qui.
          onPaste={(e) => {
            const aperta = incollaPiuRighe(e, title, (testo) =>
              useModalStore.getState().open('newTask', { prefill: { testo, assigneeId, dueDate } }));
            if (aperta) setTitle('');
          }}
          aria-label="Titolo del task"
        />
      </div>
      <select className="inp" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} aria-label="Assegna a">
        <option value="">Nessuno</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.id === meId ? 'Io' : `${u.firstName} ${u.lastName}`}</option>)}
      </select>
      <input className="inp mono" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} aria-label="Scadenza" />
      <button className="btn btn-primary" type="submit" disabled={!title.trim() || quickCreate.isPending}>
        Aggiungi
      </button>
    </form>
  );
}

// ════════════════════════════════════════════════════════════════════
// Lista
// ════════════════════════════════════════════════════════════════════
function ListView({ query, items, tab, page, onPage, hasFilters, onClearFilters, canWrite, onNew, onOpen }: {
  query: { data?: TaskListResponse; isLoading: boolean; isError: boolean };
  items: TaskItem[];
  tab: 'open' | 'done';
  page: number;
  onPage: (p: number) => void;
  hasFilters: boolean;
  onClearFilters: () => void;
  canWrite: boolean;
  onNew: () => void;
  onOpen: (id: string) => void;
}) {
  const { complete, reopen } = useTaskActions();
  const isMobile = useIsMobile();

  if (query.isLoading && !query.data) {
    return (
      <div className="ed-section">
        {/* Le stesse classi della tabella vera, tbl--card compresa: se lo
            scheletro non è fatto come la pagina, all'arrivo dei dati tutto
            salta di qualche pixel. */}
        <table className="tbl tbl--ed tbl--card"><tbody><SkeletonTable rows={6} cols={4} withAvatar={false} /></tbody></table>
      </div>
    );
  }

  if (!items.length) {
    if (hasFilters) {
      return (
        <EmptyState
          brand
          title="Nessun task con questi filtri"
          description="Azzera i filtri per vedere gli altri."
          action={<button className="btn" onClick={onClearFilters}>Azzera i filtri</button>}
        />
      );
    }
    if (tab === 'done') {
      return <EmptyState brand title="Nessun task completato" description="Quando ne chiudi uno, finisce qui." />;
    }
    return (
      <EmptyState
        brand
        title="Nessun task in lista"
        // "qui sopra" non regge sul telefono: lì il modulo veloce non c'è e
        // l'unica strada è il bottone qui sotto. La frase vale per tutti e due.
        description="Scrivi il primo: titolo, chi lo fa, entro quando."
        // d-only: sul telefono la barra in fondo porta già "Nuovo task", e i
        // due bottoni neri finivano uno sopra l'altro a quaranta pixel di
        // distanza. Il bottone di "Azzera i filtri" qui sopra invece resta:
        // quello nella barra non c'è.
        action={canWrite ? <button className="btn btn-primary d-only" onClick={onNew}><Icon name="plus" size={12} /> Nuovo task</button> : undefined}
      />
    );
  }

  const busy = complete.isPending || reopen.isPending;

  // Telefono: un'altra forma, non la tabella in schede (vedi MobileTaskList).
  if (isMobile) {
    return (
      <div className="ed-section">
        <MobileTaskList
          items={items}
          canWrite={canWrite}
          busy={busy}
          onOpen={onOpen}
          onToggle={(t) => (isDone(t.status)
            ? reopen.mutate({ id: t.id, title: t.title })
            : complete.mutate({ id: t.id, title: t.title, prevStatus: t.status, dueDate: t.dueDate, recurrence: t.recurrence }))}
        />
        {query.data && (
          <Pagination page={page} pageSize={query.data.pageSize} total={query.data.total} totalPages={query.data.totalPages} onPage={onPage} />
        )}
      </div>
    );
  }

  return (
    /* Full-bleed: la lista vive sul foglio, senza gabbia */
    <div className="ed-section">
      {/* tbl--card: sotto i 768px ogni riga diventa una scheda. Sul computer
          non fa niente, la tabella resta una tabella. Le celle dicono con
          data-m che ruolo hanno nella scheda e con data-l come si chiamano
          (l'intestazione di colonna lì non c'è più). Regole: os-planning/mobile.md */}
      <table className="tbl tbl--ed tbl--card">
        <thead>
          <tr>
            <th>Task</th>
            <th style={{ width: 190 }}>Assegnato a</th>
            <th style={{ width: 170 }}>{tab === 'done' ? 'Completato il' : 'Scadenza'}</th>
            <th style={{ width: 150 }} />
          </tr>
        </thead>
        <tbody>
          {items.map((t) => {
            const done = isDone(t.status);
            const d = dueInfo(t.dueDate, done);
            const ctx = contextLine(t);
            return (
              <tr key={t.id} onClick={() => onOpen(t.id)}>
                <td data-m="hd">
                  <div className={`flex items-center gap-2 min-w-0 ${done ? 'line-through text-mute' : 'font-medium'}`}>
                    <span className="truncate">{t.title}</span>
                    {isHigh(t.priority) && !done && <HighDot label />}
                    {t.recurrence && t.recurrence !== 'NONE' && <Icon name="repeat" size={11} className="mute-2 shrink-0" />}
                  </div>
                  {ctx && <div className="text-[12px] mute mt-0.5">{ctx}</div>}
                </td>
                <td data-m="key" data-l="Assegnato a">
                  {t.assignee ? (
                    <div className="row" style={{ gap: 6 }}>
                      <Avatar initials={t.assignee.initials} size={22} name={fullName(t.assignee)} />
                      <span className="text-[13px]">{shortName(t.assignee)}</span>
                    </div>
                  ) : <span className="mute-2">—</span>}
                </td>
                {/* Lo span serve: .tbl tbody td fissa colore E corpo (14px, e vince
                    sulle utility), quindi qui sul td si mette solo quello che funziona */}
                <td data-m="key" data-l={tab === 'done' ? 'Completato il' : 'Scadenza'} className="mono">
                  {done
                    ? <span className="mute">{fmtDate(t.completedAt)}</span>
                    : <span className={d.tone === 'alert' ? 'text-alert font-semibold' : d.tone === 'warn' ? 'text-warn' : 'mute'}>{d.label}</span>}
                </td>
                {/* data-m="row": sul telefono si prende una riga tutta sua sotto un
                    filo. È il gesto per cui si apre questa pagina dal telefono. */}
                <td data-m="row" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1 tk-row-act">
                    {canWrite && (done ? (
                      <button className="btn btn-sm" disabled={busy} title="Riapri il task" onClick={() => reopen.mutate({ id: t.id, title: t.title })}>
                        <Icon name="undo" size={10} /> Riapri
                      </button>
                    ) : (
                      <button className="btn btn-sm" disabled={busy} title="Segna come completato" onClick={() => complete.mutate({ id: t.id, title: t.title, prevStatus: t.status, dueDate: t.dueDate, recurrence: t.recurrence })}>
                        <Icon name="check" size={10} /> Completa
                      </button>
                    ))}
                    {/* Sul telefono la scheda si apre toccandola: la freccia
                        prometterebbe due volte la stessa cosa. */}
                    <button className="btn btn-sm btn-ghost btn-icon d-only" title="Apri il task" aria-label="Apri" onClick={() => onOpen(t.id)}>
                      <Icon name="arrowRight" size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {query.data && (
        <Pagination page={page} pageSize={query.data.pageSize} total={query.data.total} totalPages={query.data.totalPages} onPage={onPage} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TELEFONO — la lista dei task come la fanno le app dei promemoria
//
// Il 6/10/2026, a 390px, ogni task era una scheda alta 220px: titolo,
// "ASSEGNATO A" e "SCADENZA" in due colonne con le etichette sopra, un filo,
// e il bottone "Completa" su una riga sua. Ne stavano due e mezzo per
// schermata. Qui un task è una riga: il cerchio a sinistra lo completa con un
// tocco (lo stesso gesto, con la stessa notifica e lo stesso "Annulla"), il
// resto della riga apre il dettaglio. Sotto il titolo, in una riga di testo,
// le stesse informazioni della tabella: scadenza, priorità, chi lo fa, per
// chi. Ne stanno otto per schermata.
// Sul computer questo componente non nasce: lì resta la tabella.
// ════════════════════════════════════════════════════════════════════
export function MobileTaskList({ items, canWrite, busy, onOpen, onToggle, senzaScadenza }: {
  items: TaskItem[];
  canWrite: boolean;
  busy: boolean;
  onOpen: (id: string) => void;
  onToggle: (t: TaskItem) => void;
  /** Il gruppo dice già la scadenza ("Oggi", "Senza scadenza"): la riga non la ripete. */
  senzaScadenza?: boolean;
}) {
  return (
    <ul className="m-tasks" aria-label="Task">
      {items.map((t) => {
        const done = isDone(t.status);
        // Cliente (reso leggibile: "NEW OMNIA S.R.L." → "New Omnia S.r.l.") e codice del progetto, come contextLine.
        const ctx = [t.client?.shortName ? leggibile(t.client.shortName) : null, t.project?.code].filter(Boolean).join(' · ');
        const due = done ? null : scadenzaInParole(t.dueDate);
        return (
          <li key={t.id} className={`m-task${done ? ' is-done' : ''}`}>
            {canWrite ? (
              <button
                type="button"
                className="m-task-check"
                aria-pressed={done}
                aria-label={done ? `Riapri il task: ${t.title}` : `Segna come completato: ${t.title}`}
                disabled={busy}
                onClick={() => onToggle(t)}
              >
                <span className="m-task-ring">{done && <Icon name="check" size={13} strokeWidth={2.5} />}</span>
              </button>
            ) : (
              <span className="m-task-check" aria-hidden="true"><span className="m-task-ring">{done && <Icon name="check" size={13} strokeWidth={2.5} />}</span></span>
            )}
            <button type="button" className="m-task-body" onClick={() => onOpen(t.id)}>
              <span className="m-task-title">
                {t.title}
                {t.recurrence && t.recurrence !== 'NONE' && <Icon name="repeat" size={12} className="m-task-rep" />}
              </span>
              <span className="m-task-meta">
                {done
                  ? <span>completato il {fmtDate(t.completedAt)}</span>
                  : !senzaScadenza && <span className={due!.tone === 'alert' ? 'text-alert font-semibold' : due!.tone === 'warn' ? 'text-warn' : undefined}>{due!.testo}</span>}
                {isHigh(t.priority) && !done && <span className="text-alert">priorità alta</span>}
                <span>{t.assignee ? shortName(t.assignee) : 'non assegnato'}</span>
                {ctx && <span>{ctx}</span>}
              </span>
            </button>
            <Icon name="chevronR" size={14} className="m-task-go" />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * La scadenza in una frase che si legge da sola, senza la colonna "Scadenza"
 * accanto: "scade oggi", "scade tra 3 g", "in ritardo di 4 g", "scade il
 * 12/10/2026". Le parole e i toni sono quelli di dueInfo, come nella tabella.
 */
function scadenzaInParole(iso: string | null | undefined): { testo: string; tone: 'alert' | 'warn' | 'mute' } {
  const d = dueInfo(iso);
  if (d.days == null) return { testo: 'senza scadenza', tone: 'mute' };
  if (d.days < 0) return { testo: d.label, tone: d.tone };
  if (d.days <= 6) return { testo: `scade ${d.label}`, tone: d.tone };
  return { testo: `scade il ${d.label}`, tone: d.tone };
}

/** Priorità alta = puntino rosso (con o senza la parola "Alta"). */
function HighDot({ label }: { label?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-alert shrink-0" style={{ textDecoration: 'none' }} title="Priorità alta">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--alert)' }} />
      {label && 'Alta'}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
// Kanban a due colonne: Da fare / Completate — DnD nativo (lib/dnd.ts),
// move ottimistico (in Completate = Completa con toast/undo, in Da fare = Riapri)
// ════════════════════════════════════════════════════════════════════
type KanbanCol = 'open' | 'done';
const KANBAN_COLS: Array<{ key: KanbanCol; label: string; dot: string }> = [
  { key: 'open', label: 'Da fare', dot: 'var(--mute-2)' },
  { key: 'done', label: 'Completate', dot: 'var(--ok)' },
];

function DropZone({ dnd, col, position, visible }: {
  dnd: ReturnType<typeof useKanbanDnd<KanbanCol>>;
  col: KanbanCol;
  position: number;
  visible: boolean;
}) {
  const active = dnd.isOverZone(col, position);
  if (!visible) return null;
  return <div {...dnd.dropZoneProps(col, position)} className={`kanban-dropzone${active ? ' is-over' : ''}`} />;
}

function KanbanView({ queryKey, isLoading, items, canWrite, meId, hasFilters, onClearFilters, onOpen }: {
  queryKey: readonly unknown[];
  isLoading: boolean;
  items: TaskItem[];
  canWrite: boolean;
  meId: string;
  hasFilters: boolean;
  onClearFilters: () => void;
  onOpen: (id: string) => void;
}) {
  const { kanbanMove, quickCreate } = useTaskActions();

  // Da fare per ordine kanban (il drag riordina davvero); Completate per data di chiusura
  const columns = useMemo(() => ({
    open: items.filter((t) => !isDone(t.status)).sort((a, b) => a.order - b.order || (a.dueDate ?? '').localeCompare(b.dueDate ?? '')),
    done: items.filter((t) => isDone(t.status)).sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? '')),
  }), [items]);

  const dnd = useKanbanDnd<KanbanCol>((taskId, col, position) => {
    if (!canWrite) return;
    const task = items.find((t) => t.id === taskId);
    if (!task) return;
    const wasDone = isDone(task.status);
    const toDone = col === 'done';
    const kind = toDone && !wasDone ? 'complete' : !toDone && wasDone ? 'reopen' : 'reorder';
    if (kind === 'reorder' && toDone) return; // in Completate l'ordine è la data di chiusura: niente da fare
    // Stato di arrivo: DONE, oppure quello aperto che aveva (TODO se torna dai completati)
    const status = toDone ? 'DONE' : (wasDone ? 'TODO' : task.status);
    // Nuovo order per fractional indexing tra i vicini della colonna (escluso il task che si muove)
    const siblings = columns[col].filter((t) => t.id !== taskId);
    const p = Math.max(0, Math.min(position, siblings.length));
    let order: number;
    if (siblings.length === 0) order = 1000;
    else if (p <= 0) order = siblings[0]!.order - 1000;
    else if (p >= siblings.length) order = siblings[siblings.length - 1]!.order + 1000;
    else order = (siblings[p - 1]!.order + siblings[p]!.order) / 2;
    kanbanMove.mutate({
      id: taskId, status, position: p, order, kind, queryKey,
      title: task.title, prevStatus: task.status, dueDate: task.dueDate, recurrence: task.recurrence,
    });
  });

  // Quick-add in cima a Da fare: butta giù task al volo, resta aperto (salva-e-aggiungi-un-altro)
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickText, setQuickText] = useState('');
  const submitQuick = () => {
    const title = quickText.trim();
    if (!title) { setQuickOpen(false); return; }
    quickCreate.mutate({ title, assigneeId: meId || null });
    setQuickText('');
  };

  if (isLoading) {
    return (
      <div className="kanban tk-kanban">
        {KANBAN_COLS.map((c) => (
          <div className="kanban-col" key={c.key}>
            <div className="kanban-col-hd"><Skeleton size="sm" w={90} /><Skeleton size="sm" w={24} /></div>
            <div className="kanban-col-body"><Skeleton size="block" h={64} /><Skeleton size="block" h={64} /></div>
          </div>
        ))}
      </div>
    );
  }

  if (!items.length && hasFilters) {
    return (
      <EmptyState
        brand
        title="Nessun task con questi filtri"
        description="Azzera i filtri per vedere gli altri."
        action={<button className="btn" onClick={onClearFilters}>Azzera i filtri</button>}
      />
    );
  }

  const anyDragging = dnd.isAnyDragging();

  return (
    <div className="kanban tk-kanban">
      {KANBAN_COLS.map((c) => {
        const cards = columns[c.key];
        const isOver = dnd.isOverColumn(c.key);
        return (
          <div className={`kanban-col${isOver ? ' is-over' : ''}`} key={c.key} {...(canWrite ? dnd.columnProps(c.key) : {})}>
            <div className="kanban-col-hd">
              <div className="kanban-col-title">
                <span className="dot" style={{ background: c.dot }} />
                {c.label}
              </div>
              <span className="kanban-col-count">{cards.length}</span>
            </div>
            <div className="kanban-col-body">
              {c.key === 'open' && canWrite && (quickOpen ? (
                <input
                  className="inp text-[13px] py-1.5"
                  autoFocus
                  placeholder="Titolo, Invio per aggiungere…"
                  value={quickText}
                  onChange={(e) => setQuickText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); submitQuick(); }
                    else if (e.key === 'Escape') { setQuickOpen(false); setQuickText(''); }
                  }}
                  onBlur={() => { if (!quickText.trim()) setQuickOpen(false); }}
                  // Più righe incollate: la finestra con l'anteprima (qui la persona assegnata sono io).
                  onPaste={(e) => {
                    const aperta = incollaPiuRighe(e, quickText, (testo) =>
                      useModalStore.getState().open('newTask', { prefill: { testo, assigneeId: meId } }));
                    if (aperta) { setQuickText(''); setQuickOpen(false); }
                  }}
                  aria-label="Nuovo task"
                />
              ) : (
                <button
                  className="text-center py-2 border border-dashed border-line-2 rounded text-mute-2 text-[12px] hover:border-mute hover:text-mute w-full"
                  onClick={() => { setQuickOpen(true); setQuickText(''); }}
                >
                  <Icon name="plus" size={10} /> aggiungi
                </button>
              ))}
              <DropZone dnd={dnd} col={c.key} position={0} visible={anyDragging} />
              {cards.map((t, idx) => {
                const done = c.key === 'done';
                const d = dueInfo(t.dueDate, done);
                return (
                  <Fragment key={t.id}>
                    <div
                      className="kanban-card cursor-grab active:cursor-grabbing"
                      {...(canWrite ? dnd.cardProps(t.id, c.key) : {})}
                      onClick={() => onOpen(t.id)}
                    >
                      <div className={`kanban-card-title flex items-start gap-2${done ? ' line-through text-mute' : ''}`}>
                        <span className="min-w-0 flex-1">{t.title}</span>
                        {isHigh(t.priority) && !done && <span className="mt-1.5"><HighDot /></span>}
                      </div>
                      <div className="row between">
                        {t.assignee ? (
                          <div className="row text-[12px]" style={{ gap: 4 }}>
                            <Avatar initials={t.assignee.initials} size={18} name={fullName(t.assignee)} />
                            <span className="mute">{shortName(t.assignee)}</span>
                          </div>
                        ) : <span />}
                        <span className={`mono text-[12px] inline-flex items-center gap-1 ${d.tone === 'alert' ? 'text-alert font-semibold' : d.tone === 'warn' ? 'text-warn' : 'mute'}`}>
                          {t.recurrence && t.recurrence !== 'NONE' && <Icon name="repeat" size={10} className="mute-2" />}
                          {done ? fmtDate(t.completedAt) : d.label}
                        </span>
                      </div>
                    </div>
                    {/* In Completate la posizione non conta: una sola zona di drop (la colonna) */}
                    {c.key === 'open' && <DropZone dnd={dnd} col={c.key} position={idx + 1} visible={anyDragging} />}
                  </Fragment>
                );
              })}
              {!cards.length && !anyDragging && (
                <div className="text-center py-6 mute-2 text-[12px]">
                  {c.key === 'open' ? 'Niente da fare.' : 'Trascina qui un task per completarlo.'}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
